import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState } from 'react'
import { ActionButton } from '~/team_tools/components/ActionButton'
import { useNotification } from '~/team_tools/components/NotificationProvider'
import { ActionRequest, approveAction, rejectAction } from './api'

// Renders the evidence a proposal carries rather than just its id. A reviewer
// approving a destroy needs to see how many players are on the server; one
// approving a configure needs to see the name and fee it will set. A queue that
// only shows "SERVER_DESTROY #12" gets rubber-stamped.
// Kind numbers from team_tools.models.ActionKind.
const KIND_SERVER_CONFIGURE = 1
const KIND_SERVER_RESTART = 4

const fee = (value: unknown): string =>
    value === null || value === undefined || value === '' ? 'unset' : String(value)

const summarise = (request: ActionRequest): string => {
    const { payload, evidence } = request
    // Checked before the payload shape: a restart carries only `server_id`,
    // which the destroy branch below would read as "destroy … (database row only)".
    if (request.kind === KIND_SERVER_RESTART) {
        const players = evidence.players_online
        const previous = evidence.previous_restarts_in_window
        return [
            `restart the game container on ${evidence.slot ?? `#${payload.server_id}`}`,
            evidence.diagnosis ? `(${evidence.diagnosis})` : '',
            players ? `· ⚠ ${players} player(s) online` : '',
            previous ? `· restarted ${previous} time(s) recently` : '',
        ]
            .filter(Boolean)
            .join(' ')
    }
    if (request.kind === KIND_SERVER_CONFIGURE || payload.name) {
        // Approving this re-prices the server, so the fee leads, with what it
        // is now when the proposal recorded it.
        const priced =
            'current_fee' in evidence && fee(evidence.current_fee) !== fee(payload.fee)
                ? `fee ${fee(evidence.current_fee)} → ${fee(payload.fee)}`
                : `fee ${fee(payload.fee)}`
        return [
            `${evidence.slot ?? ''} → "${payload.name}"${payload.activate ? ' and set active' : ''}`,
            `· ${priced}`,
            payload.max_players ? `· max ${payload.max_players} players` : '',
            payload.map_id !== undefined && payload.map_id !== null ? `· map ${payload.map_id}` : '',
            `· ${payload.password ? 'password set' : 'no password'}`,
        ]
            .filter(Boolean)
            .join(' ')
    }
    if (payload.slot && !payload.server_id) {
        return `build a new server in slot ${payload.slot}`
    }
    if (payload.server_id) {
        const players = evidence.players_online
        return [
            `destroy ${evidence.slot ?? `#${payload.server_id}`}`,
            payload.destroy_machine ? 'and tear down the VM' : '(database row only)',
            players ? `· ⚠ ${players} player(s) online` : '',
        ]
            .filter(Boolean)
            .join(' ')
    }
    // The auto-mod and cheat kinds carry the accounts they act on rather than a
    // server. The first few fields, as the admin's own summary column shows them.
    const fields = Object.entries(payload)
        .slice(0, 3)
        .map(([k, v]) => `${k}=${Array.isArray(v) ? `${v.length} item(s)` : v}`)
    return fields.length ? fields.join(', ') : request.kind_label
}

// canDecide: approving or rejecting is DeveloperPermission, or a per-kind grant
// from the admin. Defaults on for the older screen that renders this without
// it. A row the server marked `can_decide: false` stays disabled either way:
// the queue can mix kinds this viewer may decide with kinds they may not.
// approveAll: the "Approve all" button, off by default. On for the server
// screens, where the queue is destroys and builds; never on Anti Cheat, where
// one click would ban every account in the list.
export const ApprovalQueue = ({ requests, onChange, canDecide = true, approveAll: allowAll = false }) => {
    const { addNotification } = useNotification()
    const [notes, setNotes] = useState<Record<number, string>>({})

    const decide = (request: ActionRequest, approve: boolean) => async () => {
        try {
            await (approve ? approveAction : rejectAction)(request.id, notes[request.id] ?? '')
            addNotification(
                `${approve ? 'Approved' : 'Rejected'} request #${request.id}`,
                'success'
            )
        } catch (err) {
            addNotification(err.message, 'error')
        } finally {
            onChange()
        }
    }

    // Every request in this list the viewer may decide, approved one after
    // another with the note typed on each row. Sequential rather than all at
    // once: each approval runs its executor (a destroy tears a VM down), and
    // firing them together would have them racing for the same server rows.
    // Your own destroys, configures, money, ban and pause proposals are left
    // out: the server refuses them from the person who raised them (D-048).
    const decidable = requests.filter(
        (r: ActionRequest) => canDecide && r.can_decide !== false && !r.own_needs_second
    )
    const approveAll = async () => {
        let done = 0
        const failed: string[] = []
        for (const request of decidable) {
            try {
                await approveAction(request.id, notes[request.id] ?? '')
                done += 1
            } catch (err) {
                failed.push(`#${request.id}: ${err.message}`)
            }
        }
        addNotification(
            failed.length
                ? `Approved ${done} of ${decidable.length}. Failed: ${failed.join('; ')}`
                : `Approved all ${done} request${done === 1 ? '' : 's'}`,
            failed.length ? 'error' : 'success'
        )
        onChange()
    }

    if (!requests.length) {
        return (
            <div className='text-xs opacity-50 border border-white/10 rounded p-3'>
                Nothing awaiting review.
            </div>
        )
    }

    return (
        <div className='flex flex-col gap-2'>
            {allowAll && decidable.length > 1 && (
                <div className='flex flex-row justify-end'>
                    <ActionButton onClick={approveAll} className='text-green-400'>
                        <FontAwesomeIcon icon={faCheck} className='mr-1' /> Approve all ({decidable.length})
                    </ActionButton>
                </div>
            )}
            {requests.map((request: ActionRequest) => (
                <div
                    key={request.id}
                    className='flex flex-row items-center gap-3 border border-amber-500/40 bg-amber-500/5 rounded px-3 py-2 text-xs'
                >
                    <span className='opacity-50 shrink-0'>#{request.id}</span>
                    <span className='font-bold shrink-0'>{request.kind_label}</span>
                    <span className='grow'>{summarise(request)}</span>
                    {request.error && <span className='text-red-400'>{request.error}</span>}
                    <input
                        placeholder='note (optional)'
                        value={notes[request.id] ?? ''}
                        onChange={(e) =>
                            setNotes((prev) => ({ ...prev, [request.id]: e.target.value }))
                        }
                        className='bg-transparent border border-white/20 rounded px-2 py-1 w-40 shrink-0'
                    />
                    <ActionButton onClick={decide(request, true)} disabled={!canDecide || request.can_decide === false} className='text-green-400 shrink-0'>
                        <FontAwesomeIcon icon={faCheck} className='mr-1' /> Approve
                    </ActionButton>
                    <ActionButton onClick={decide(request, false)} disabled={!canDecide || request.can_decide === false} className='text-red-400 shrink-0'>
                        <FontAwesomeIcon icon={faXmark} className='mr-1' /> Reject
                    </ActionButton>
                </div>
            ))}
        </div>
    )
}
